import { SdkContext } from '../internal'

/** F-34: an entitlement value — an integer, a boolean or `unlimited`. */
export type EntitlementValue = number | boolean | 'unlimited'

export interface EntitlementCatalogEntry {
  key: string
  subject: 'community'
  type: 'bool' | 'int' | 'limit'
  unit: string | null
  default: EntitlementValue
  addOnMode: 'sum' | 'or' | null
  editableBy: 'superAdmin'
}

/** Where a value came from. An exception never carries its reason here. */
export interface EntitlementSource {
  layer: 'default' | 'globalDefault' | 'plan' | 'addOn' | 'override' | 'platformHome'
  ref?: string | null
  validUntil?: string | null
  value?: EntitlementValue
}

/**
 * F-34 quota period: `month` resets on day 1 in the community's zone (what is left
 * is lost); `annual-term` (a yearly plan) accumulates the monthly quota over the
 * term and resets at the renewal.
 */
export type QuotaPeriod = 'month' | 'annual-term'

/**
 * A plan quota for "X of Y". `limit` is always the **monthly** value; `available`
 * is what is left in the period (with the accumulated part on an annual plan),
 * `null` when unlimited. `percent` is used over the period's total, null when the
 * limit is `unlimited` or zero.
 */
export interface QuotaMeter {
  used: number
  limit: number | 'unlimited'
  percent: number | null
  period?: QuotaPeriod
  available?: number | null
  resetsAt?: string
  /** Native video only: what is left of the one-time trial gift (used before the plan's quota). */
  trialMinutesLeft?: number | null
}

/** Kept for the callers that still name it. */
export type AiUsageMeter = QuotaMeter

export interface CommunityEntitlements {
  values: Record<string, EntitlementValue>
  sources?: Record<string, EntitlementSource[]>
  plan: {
    subscriptionId: string
    status: string
    entitling: boolean
    planNames: string[]
    billingOption: string | null
    currentPeriodStart: string | null
    currentPeriodEnd: string | null
    addOns: Array<{ subscriptionId: string; title: string }>
  } | null
  isPlatformHome: boolean
  members: { used: number; limit: number | 'unlimited'; percent: number | null }
  /** This month's AI usage (from `periodStart`, the 1st in the community's time zone); minutes rounded up. */
  ai?: {
    periodStart: string
    period?: QuotaPeriod
    resetsAt?: string
    transcriptionMinutes: QuotaMeter
    textUses: QuotaMeter
  }
  /** Video converted for adaptive streaming, by source length (`video.transcodeMinutesPerMonth`). */
  video?: {
    periodStart: string
    period?: QuotaPeriod
    resetsAt?: string
    transcodeMinutes: QuotaMeter
    /** The one-time native video gift granted (0 when none); its use is not in `transcodeMinutes.used`. */
    trialMinutes?: number | null
    /** What is left of that gift. */
    trialMinutesLeft?: number | null
  }
  /** E-mail recipients of campaign sends (`campaigns.recipientsPerMonth`). */
  campaigns?: { recipients: QuotaMeter }
  modules: Record<string, { locked: boolean }>
}

/** A SuperAdmin exception; `reason` is internal and never reaches the community. */
export interface EntitlementOverride {
  id: string
  key: string
  value: EntitlementValue
  reason: string | null
  validFrom: string | null
  validUntil: string | null
  createdByMemberId: string | null
  updatedByMemberId: string | null
  createdAt: string
  updatedAt: string
}

/** One plan (`subscriptionGroupId`) or one product (billing option or add-on). */
export type EntitlementOwnerRef = { subscriptionGroupId: string } | { productId: string }

export interface EntitlementOwnerValues {
  owner: { subscriptionGroupId?: string | null; productId?: string | null; name?: string; kind?: 'plan' | 'product' }
  values: Record<string, EntitlementValue>
}

export interface OverrideInput {
  key?: string
  value?: EntitlementValue
  reason?: string | null
  validFrom?: string | null
  validUntil?: string | null
}

/**
 * F-34: what a Memberfy plan gives a community. `catalog` and `community` are
 * the community's view (owner/admin); everything under `/admin` is the
 * SuperAdmin's, from the Memberfy community only (403 anywhere else).
 */
export class EntitlementsResource {
  constructor(private ctx: SdkContext) {}

  async catalog(): Promise<{ entitlements: EntitlementCatalogEntry[] }> {
    return this.ctx.call({ httpMethod: 'GET', path: '/entitlements/catalog' })
  }

  /** Owner and admin of the `X-CommunityId`: values, plan, member usage, locked extensions. */
  async community(): Promise<CommunityEntitlements> {
    return this.ctx.call({ httpMethod: 'GET', path: '/entitlements/community' })
  }

  async getValues(owner: EntitlementOwnerRef): Promise<EntitlementOwnerValues> {
    return this.ctx.call({ httpMethod: 'GET', path: '/admin/entitlements/values' }, { query: owner })
  }

  /** `null` clears a key on that owner. */
  async setValues(owner: EntitlementOwnerRef, values: Record<string, EntitlementValue | null>): Promise<EntitlementOwnerValues> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: '/admin/entitlements/values', hasBody: true },
      { body: { ...owner, values } }
    )
  }

  /** `sources[key]`: `stored` (the SuperAdmin set it) or `catalog` (the catalog default). */
  async getDefaults(): Promise<{ values: Record<string, EntitlementValue>; sources?: Record<string, 'stored' | 'catalog'> }> {
    return this.ctx.call({ httpMethod: 'GET', path: '/admin/entitlements/defaults' })
  }

  /** `null` returns a key to the catalog default. */
  async setDefaults(values: Record<string, EntitlementValue | null>): Promise<{ values: Record<string, EntitlementValue>; sources?: Record<string, 'stored' | 'catalog'> }> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: '/admin/entitlements/defaults', hasBody: true },
      { body: { values } }
    )
  }

  async listOverrides(communityId: string): Promise<{ overrides: EntitlementOverride[]; effective: CommunityEntitlements }> {
    return this.ctx.call(
      { httpMethod: 'GET', path: '/admin/communities/{communityId}/entitlements/overrides' },
      { communityId }
    )
  }

  async createOverride(communityId: string, input: OverrideInput): Promise<EntitlementOverride> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/admin/communities/{communityId}/entitlements/overrides', hasBody: true },
      { communityId, body: input }
    )
  }

  async updateOverride(communityId: string, id: string, input: OverrideInput): Promise<EntitlementOverride> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: '/admin/communities/{communityId}/entitlements/overrides/{id}', hasBody: true },
      { communityId, id, body: input }
    )
  }

  /** Ends an exception (the row is kept); the community goes back to what its plan gives. */
  async endOverride(communityId: string, id: string): Promise<unknown> {
    return this.ctx.call(
      { httpMethod: 'DELETE', path: '/admin/communities/{communityId}/entitlements/overrides/{id}' },
      { communityId, id }
    )
  }
}
